import { DATE_FE_FORMAT, TIME_FORMAT } from '@/constants';
import { DispatchIssueWithCache, DispatchIssueWithCacheRecoveryNeededEnum, DispatchIssueWithCacheTaxiUsedEnum } from '@/models/gen/graphql';
import { ForwardRefExoticComponent, forwardRef } from 'react';

import { Datetime } from '@/utils';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { useIssueHistoryDrawer } from '@/features/IssueHistory/components/IssueHistoryDrawer';
import { useProvider } from '@/common/Provider';

const IssueFormHistoryTableRow = ({ id }: { id: string }, ref: ForwardRefExoticComponent<HTMLDivElement>): JSX.Element => {
  const [[dispatchIssues, , , vehicleTrackingId], , { onEditDispatchIssue }] = useProvider<
    { data: Map<string, DispatchIssueWithCache>; driverFirstName: string; driverLastName: string; vehicleTrackingId: string },
    [DispatchIssueWithCache, string, string, string],
    { onEditDispatchIssue: (accident: DispatchIssueWithCache) => void }
  >(({ data: dispatchIssues, driverFirstName, driverLastName, vehicleTrackingId }) => [
    dispatchIssues.get(id) || ({} as DispatchIssueWithCache),
    driverFirstName,
    driverLastName,
    vehicleTrackingId,
  ]);
  const [state, setIssueHistoryDrawer] = useIssueHistoryDrawer(({ state, setState }) => [state, setState]);

  const onEdit = (): void => {
    if (onEditDispatchIssue) return onEditDispatchIssue(dispatchIssues);
    setIssueHistoryDrawer((current) => ({
      ...current,
      show: true,
      data: dispatchIssues,
    }));
  };

  const isRecoveryNeeded = dispatchIssues.recoveryNeeded === DispatchIssueWithCacheRecoveryNeededEnum.Yes ? 'Recovery Needed' : undefined;
  const wasTaxiCalled = dispatchIssues.taxiUsed === DispatchIssueWithCacheTaxiUsedEnum.Yes ? 'Taxi Called' : undefined;
  const taxiCost = `$${dispatchIssues.taxiCost?.toFixed(2)}`;
  const driverName = [dispatchIssues.driverFirstName, dispatchIssues.driverLastName].join(' ');
  const vanNumber = vehicleTrackingId ? `#${vehicleTrackingId}` : null;

  return (
    <SimpleTableRow id={id} onDoubleClick={onEdit} ref={ref} className="incidentHistoryRecord" visible>
      <SimpleTableCell name="DISPATCH_ISSUE" className="w-md-grow">
        <span>{[driverName, vanNumber].filter(Boolean).join(' - ')}</span>
        <span>{[isRecoveryNeeded, wasTaxiCalled, taxiCost].filter(Boolean).join(' - ')}</span>
      </SimpleTableCell>
      <SimpleTableCell name="CREATED_AT">
        <span>{new Datetime(dispatchIssues.createdAt).format(DATE_FE_FORMAT)}</span>
        <span>{new Datetime(dispatchIssues.createdAt).format(TIME_FORMAT)}</span>
      </SimpleTableCell>
    </SimpleTableRow>
  );
};

export default forwardRef(IssueFormHistoryTableRow);
